import { useState } from "react";
import { Calendar, Clock, Phone, Mail, User, Smartphone, Laptop, Tablet, Gamepad2, HelpCircle, Send, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const deviceTypes = [
  { value: "telephone", label: "Téléphone", icon: Smartphone },
  { value: "tablette", label: "Tablette", icon: Tablet },
  { value: "ordinateur", label: "Ordinateur", icon: Laptop },
  { value: "console", label: "Console", icon: Gamepad2 },
  { value: "autre", label: "Autre", icon: HelpCircle },
];

const timeSlots = [
  "10:00", "10:30", "11:00", "11:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
];

export const AppointmentForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    device_type: "",
    preferred_date: "",
    preferred_time: "",
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/api/appointment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Erreur lors de l'envoi");
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        device_type: "",
        preferred_date: "",
        preferred_time: "",
        notes: ""
      });
    } catch (err) {
      setError(err.message || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // Get maximum date (2 months from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 2);
    return maxDate.toISOString().split("T")[0];
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="appointment-success">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-3">
          Rendez-vous demandé !
        </h3>
        <p className="text-slate-600 mb-6">
          Nous avons bien reçu votre demande de rendez-vous. Notre équipe vous contactera dans les plus brefs délais pour confirmer le créneau.
        </p>
        <Button 
          onClick={() => setIsSubmitted(false)}
          className="bg-red-600 hover:bg-red-700"
          data-testid="new-appointment-btn"
        >
          Prendre un autre rendez-vous
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8" data-testid="appointment-form">
      <div className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-500" />
              Nom complet *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              placeholder="Jean Dupont"
              value={formData.name}
              onChange={handleChange}
              className="h-12"
              data-testid="appointment-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-slate-500" />
              Téléphone *
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              placeholder="06 12 34 56 78"
              value={formData.phone}
              onChange={handleChange}
              className="h-12"
              data-testid="appointment-phone"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-500" />
            Email *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            placeholder="votre@email.com"
            value={formData.email}
            onChange={handleChange}
            className="h-12"
            data-testid="appointment-email"
          />
        </div>

        {/* Device Type */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-slate-500" />
            Type d&apos;appareil *
          </Label>
          <Select value={formData.device_type} onValueChange={(value) => handleSelectChange("device_type", value)} required>
            <SelectTrigger className="h-12" data-testid="appointment-device-type">
              <SelectValue placeholder="Sélectionnez le type d'appareil" />
            </SelectTrigger>
            <SelectContent>
              {deviceTypes.map((device) => {
                const Icon = device.icon;
                return (
                  <SelectItem key={device.value} value={device.value}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {device.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="preferred_date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              Date souhaitée *
            </Label>
            <Input
              id="preferred_date"
              name="preferred_date"
              type="date"
              required
              min={getMinDate()}
              max={getMaxDate()}
              value={formData.preferred_date}
              onChange={handleChange}
              className="h-12"
              data-testid="appointment-date"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" />
              Heure souhaitée *
            </Label>
            <Select value={formData.preferred_time} onValueChange={(value) => handleSelectChange("preferred_time", value)} required>
              <SelectTrigger className="h-12" data-testid="appointment-time">
                <SelectValue placeholder="Sélectionnez l'heure" />
              </SelectTrigger>
              <SelectContent>
                {timeSlots.map((time) => (
                  <SelectItem key={time} value={time}>
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor="notes">
            Notes ou description du problème (optionnel)
          </Label>
          <Textarea
            id="notes"
            name="notes"
            placeholder="Décrivez brièvement le problème ou toute information utile..."
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="resize-none"
            data-testid="appointment-notes"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="appointment-error">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 bg-red-600 hover:bg-red-700 text-lg font-semibold gap-2"
          data-testid="appointment-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              Demander un rendez-vous
            </>
          )}
        </Button>

        <p className="text-sm text-slate-500 text-center">
          Nous vous recontacterons rapidement pour confirmer votre créneau.
          <br />
          <strong>Diagnostic gratuit</strong> pour toute réparation.
        </p>
      </div>
    </form>
  );
};
